// material
import React, { useState, useEffect } from 'react';
import { filter } from 'lodash';
import {
  Box,
  Card,
  Table,
  Stack,
  TableRow,
  Paper,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  CircularProgress,
  useTheme,
} from '@mui/material';

// components
import AdminMoreMenu from '../sections/@dashboard/user/AdminMoreMenu';
import CustomPagination from '../components/CustomPagination';
import CreateButton from '../components/CreateButton';
import PageOperator from '../components/dashboard/PageOperator';
import MoreOperator from '../components/dashboard/MoreOperator';
import { usePrivilege } from '../PrivilegeContext';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import SnackBar from '../components/Snackbar';
import {
  APIGetOperator, APIDeleteOperator, APIGetAdminUser, APIGetZones,
} from '../utils/apiCalled';

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    return filter(array, (_operator) => (
      _operator.operatorName.toLowerCase().indexOf(query.toLowerCase()) !== -1
    ));
  }

  return stabilizedThis.map((el) => el[0]);
}

const TABLE_HEAD = [
  { id: 'operatorID', label: 'Operator ID', alignRight: false },
  { id: 'operatorName', label: 'Operator Name', alignRight: false },
  { id: 'operatorVehicles', label: 'Number of Operator Vehicles', alignRight: false },
  { id: 'operatorZone', label: 'Number of Operator Zone', alignRight: false },
  { id: 'numberOfAdmins', label: 'Number of Admins assigned', alignRight: false },
  { id: '' },
];

const tableStyle = {
  border: '0.063rem solid #EAECF0',
  borderTop: '0.094rem solid #EAECF0',
  borderBottom: '0.094rem solid #EAECF0',
  boxShadow: '0rem 0.063rem 0.188rem rgba(16, 24, 40, 0.1), 0rem 0.063rem 0.125rem rgba(16, 24, 40, 0.06)',
  borderRadius: '0.75rem',
};

const loadingStyle = {
  position: 'absolute',
  zIndex: 10,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

// ----------------------------------------------------------------------

export default function Operator() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [variant, setVariant] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [succMsg, setSuccMsg] = useState('');
  const [mainMsg, setMainMsg] = useState('');
  const [posLoading, setPosLoading] = useState(false);
  const [getOperator, setGetOperator] = useState([]);
  const [totalOperators, setTotalOperators] = useState(0);
  const [adminsByOperator, setAdminsByOperator] = useState({});
  const [zones, setZones] = useState([]);
  const [assignedZones, setAssignedZones] = useState([]);
  const { role } = usePrivilege();
  const theme = useTheme();
  // modals
  const [openMoreOperator, setOpenMoreOperator] = useState(false);
  const [openPageOperator, setOpenPageOperator] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalTriggerAPICalled, setModalTriggerAPICalled] = useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // fetch operators
  const getAPICalled = async () => {
    try {
      const result = await APIGetOperator(setLoading);
      if (result.status === 200) {
        const { data } = result;
        setTotalOperators(data.length);
        setGetOperator(data);
      } else {
        setOpen(true);
        setVariant('error');
        setMainMsg('Error Encountered');
        setErrMsg('Error!, Please try again');
      }
    } catch (err) {
      setOpen(true);
      setVariant('error');
      setMainMsg('Error Encountered');
      setErrMsg('Error!', err);
    } finally {
      setLoading(false);
    }
  };

  // fetch all admins, group by operatorId
  const getAdminsCalled = async () => {
    try {
      const result = await APIGetAdminUser(setLoading);
      if (result.status === 200 && Array.isArray(result.data)) {
        const grouped = {};
        result.data.forEach((user) => {
          if (user.assignedOperators) {
            user.assignedOperators.forEach((opId) => {
              if (!grouped[opId]) grouped[opId] = [];
              grouped[opId].push(user);
            });
          }
        });
        setAdminsByOperator(grouped);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await APIGetZones(setPosLoading);
        if (res.status === 200) {
          setZones(res.data);
        } else {
          console.error('Failed to fetch zones:', res);
        }
      } catch (err) {
        console.error('Error fetching zones:', err);
      }
    };
    fetchZones();
  }, []);

  useEffect(() => {
    getAPICalled();
    getAdminsCalled();
  }, []);

  useEffect(() => {
    if (modalTriggerAPICalled) {
      getAPICalled();
      getAdminsCalled();
      setModalTriggerAPICalled(false);
    }
  }, [modalTriggerAPICalled]);

  const handleOpenMoreOperator = (operator) => {
    setSelectedOperator(operator);
    setOpenMoreOperator(true);
    setOpenPageOperator(false);
  };

  const handleCloseMoreOperators = () => {
    setSelectedOperator(false);
    setOpenMoreOperator(false);
    setOpenPageOperator(false);
  };

  const handleOpenPageOperator = (operator) => {
    setSelectedOperator(operator);
    setOpenMoreOperator(false);
    setOpenPageOperator(true);
  };

  const handleOpenPageOperatorCreate = () => {
    setOpenMoreOperator(false);
    setOpenPageOperator(true);
  };

  const handleDelete = async (data) => {
    try {
      const result = await APIDeleteOperator(setLoading, data._id);
      if (result.status === 200) {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('success');
        setMainMsg('Operator Successfully Deleted');
        setSuccMsg(`Operator ${data.operatorName} has successfully been deleted`);
        setOpenMoreOperator(false);
        setModalTriggerAPICalled(true);
      } else {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setMainMsg('Error Deleting Operator');
        setVariant('error');
        setErrMsg('Error deleting operator!, Please try again');
      }
    } catch (err) {
      setLoading(false);
      setDeleteModal(false);
      setOpen(true);
      setMainMsg('Error Deleting Operator');
      setVariant('error');
      setErrMsg('Error deleting operator!, Please try again');
    }
  };

  const filteredOperators = applySortFilter(getOperator, getComparator(order, orderBy), filterName);

  return (
    <Page title="Operators">
      <Container maxWidth="xl" sx={{ mt: (!openMoreOperator && !openPageOperator) ? 12 : 0 }}>
        <SnackBar
          open={open}
          alert={variant}
          handleClose={() => setOpen(false)}
          errorMsg={errMsg}
          successMsg={succMsg}
          mainMsg={mainMsg}
        />
        {(!openMoreOperator && !openPageOperator) && (
        <>
          <Box sx={{ pb: 5 }}>
            <Typography variant="h3">Operators</Typography>
            <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
              Manage Trek Rides operator details
            </Typography>
          </Box>
          {loading && <CircularProgress color="success" sx={loadingStyle} />}
          <Box sx={tableStyle}>
            <Box sx={{
              mr: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
            >
              <UserListToolbar
                numSelected={selected.length}
                filterName={filterName}
                onFilterName={(e) => setFilterName(e.target.value)}
                labelName="Search Operator Name"
              />
              {(role === 900 || role === 101 || role === 111) && (
              <CreateButton type="Operator" handleOpen={handleOpenPageOperatorCreate} />
              )}
            </Box>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={getOperator.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={() => {}}
                  />
                  <TableBody>
                    {filteredOperators
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const {
                          _id, operatorID, operatorName, operatorAddress,
                        } = row;
                        const adminCount = adminsByOperator[_id]?.length || 0;
                        const zoneCount = Array.isArray(assignedZones) ? assignedZones.length : 0;
                        return (
                          <TableRow hover key={_id} onClick={() => handleOpenMoreOperator(row)}>
                            <TableCell>{operatorID}</TableCell>
                            <TableCell>{operatorName}</TableCell>
                            <TableCell>{row.vehicleCount || 0}</TableCell>
                            <TableCell>{zoneCount}</TableCell>
                            <TableCell>{adminCount}</TableCell>
                            <TableCell align="right">
                              <AdminMoreMenu
                                handleSeeMore={handleOpenMoreOperator}
                                handleEditOpen={handleOpenPageOperator}
                                data={row}
                                handleDeleteOpen={() => setDeleteModal(true)}
                                handleClose={() => setDeleteModal(false)}
                                deleteModal={deleteModal}
                                handleDelete={handleDelete}
                                setModalTriggerAPICalled={setModalTriggerAPICalled}
                                setSuccMsg={setSuccMsg}
                                setErrMsg={setErrMsg}
                                setVariant={setVariant}
                                setOpen={setOpen}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
            <CustomPagination
              page={page}
              totalNum={filteredOperators.length}
              rowsPerPage={rowsPerPage}
              handleChangePage={(e, newPage) => setPage(newPage)}
            />
          </Box>
        </>
        )}
        {openPageOperator && (
          <PageOperator
            handleCloseMoreOperators={handleCloseMoreOperators}
            operator={selectedOperator}
            setOpen={setOpen}
            setSuccMsg={setSuccMsg}
            setErrMsg={setErrMsg}
            setVariant={setVariant}
            setMainMsg={setMainMsg}
            setModalTriggerAPICalled={setModalTriggerAPICalled}
            zones={zones}
            role={role}
          />
        )}
        {openMoreOperator && (
          <MoreOperator
            handleCloseMoreOperators={handleCloseMoreOperators}
            operator={selectedOperator}
            handleDelete={handleDelete}
            deleteModal={deleteModal}
            handleOpenDeleteModal={() => setDeleteModal(true)}
            handleCloseDeleteModal={() => setDeleteModal(false)}
            handleOpenPageOperator={handleOpenPageOperator}
            zones={zones}
            role={role}
            filteredUsers={adminsByOperator[selectedOperator?._id] || []}
          />
        )}
      </Container>
    </Page>
  );
}
